"use client";

import { useRef } from "react";
import { SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import { COMPLIANCE, COMPLIANCE_COLORS } from "./data";

/** Status pill styling — fg = status color, bg 10% on white, border 35%. */
const statusPill = (kind: string) => {
  const color = COMPLIANCE_COLORS[kind];
  return {
    color,
    background: `color-mix(in srgb, ${color} 10%, white)`,
    borderColor: `color-mix(in srgb, ${color} 35%, transparent)`,
  };
};

/** Compliance — the questionnaire checklist, pre-checked. */
export default function ComplianceSection() {
  const rootRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-comp-row]", { y: 12, stagger: 0.06 });

  return (
    <SectionShell id="compliance" className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px">
      <div ref={rootRef}>
        <SectionHead
          eyebrow="Compliance"
          title="The checklist, pre-checked."
          lead="Forward this section to whoever owns the questionnaire — most of it is already answered."
        />
        {/* Plain block wrapper — the row-snap padding must land OUTSIDE the
            bordered card, not inside it. */}
        <div data-grid-block>
          <div className="overflow-hidden rounded-ref-lg border border-line bg-surface shadow-ref">
            {COMPLIANCE.map((item) => (
            <div
              key={item.name}
              data-comp-row
              className={`grid grid-cols-[1fr_auto] items-center gap-x-[18px] gap-y-1 border-t border-line px-6 py-[15px] first:border-t-0 md:grid-cols-[minmax(120px,150px)_1fr_auto] ${
                item.highlight ? "bg-brand-600/[0.03]" : ""
              }`}
            >
              <span className="text-[13.5px] font-bold">{item.name}</span>
              <span className="text-[12.5px] leading-[1.5] text-muted max-md:col-span-2 max-md:row-start-2">
                {item.desc}
              </span>
              <span
                className="justify-self-end whitespace-nowrap rounded-full border px-2.5 py-[3px] font-jetbrains text-[10px]"
                style={statusPill(item.kind)}
              >
                {item.badge}
              </span>
            </div>
            ))}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
